import { useState, useEffect, useCallback } from "react";
import type { FeedbackItem, FeedbackType, FeedbackStatus } from "../../../api/types";
import { getUserFacingMessage, showAlert } from "../../../api/errors";
import {
  fetchPlatformFeedbackItems,
  putPlatformScopedFeedbackItem,
  putTenantFeedbackItem,
} from "../../../api/feedback";

const TYPE_CONFIG: Record<FeedbackType, { label: string; color: string; icon: string }> = {
  试用申请: { label: "试用申请", color: "bg-amber-50 text-amber-800", icon: "ri-rocket-line" },
  "功能建议": { label: "功能建议", color: "bg-teal-50 text-teal-700", icon: "ri-lightbulb-line" },
  "Bug反馈": { label: "Bug反馈", color: "bg-red-50 text-red-700", icon: "ri-bug-line" },
  "活动建议": { label: "活动建议", color: "bg-amber-50 text-amber-700", icon: "ri-calendar-2-line" },
  "会员服务": { label: "会员服务", color: "bg-orange-50 text-orange-700", icon: "ri-service-line" },
  "其他": { label: "其他", color: "bg-gray-100 text-gray-600", icon: "ri-more-line" },
};

const STATUS_CONFIG: Record<FeedbackStatus, { label: string; color: string; dot: string }> = {
  unread: { label: "未读", color: "bg-red-100 text-red-700", dot: "bg-red-500" },
  processing: { label: "处理中", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  resolved: { label: "已回复", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
};

async function persistFeedbackUpdate(item: FeedbackItem, body: { status?: FeedbackStatus; reply?: string; handledByLabel?: string | null }) {
  if (item.tenantId) {
    await putTenantFeedbackItem(item.tenantId, item.id, body);
  } else {
    await putPlatformScopedFeedbackItem(item.id, body);
  }
}

export default function FeedbackCenter() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<FeedbackItem | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | FeedbackStatus>("all");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const list = await fetchPlatformFeedbackItems();
      setItems(list);
      return list;
    } catch (e) {
      setListError(getUserFacingMessage(e, "反馈列表加载失败，请刷新页面。"));
      setItems([]);
      return [];
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const filtered = items.filter((item) => filterStatus === "all" || item.status === filterStatus);
  const unreadCount = items.filter((i) => i.status === "unread").length;

  const stats = {
    total: items.length,
    unread: items.filter((i) => i.status === "unread").length,
    processing: items.filter((i) => i.status === "processing").length,
    resolved: items.filter((i) => i.status === "resolved").length,
  };

  const handleSelect = async (item: FeedbackItem) => {
    setSelectedItem(item);
    if (item.status === "unread") {
      try {
        await persistFeedbackUpdate(item, { status: "processing" });
        const list = await fetchPlatformFeedbackItems();
        setItems(list);
        const u = list.find((i) => i.id === item.id);
        if (u) setSelectedItem(u);
      } catch {
        await reload();
      }
    }
  };

  const handleMarkResolved = async (item: FeedbackItem) => {
    try {
      await persistFeedbackUpdate(item, { status: "resolved" });
      const list = await fetchPlatformFeedbackItems();
      setItems(list);
      setSelectedItem((p) => (p?.id === item.id ? list.find((i) => i.id === item.id) ?? null : p));
    } catch {
      showAlert("更新失败");
    }
  };

  const tenantLabel = (item: FeedbackItem) => item.tenantName || item.tenantId || "平台";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-800">用户反馈中心</h2>
          <p className="text-xs text-gray-400 mt-0.5">汇总各租户会员端用户提交的意见与建议</p>
        </div>
        <div className="flex items-center gap-2">
          {listLoading && <span className="text-xs text-gray-400">加载中…</span>}
          {listError && <span className="text-xs text-red-500 max-w-[200px] truncate" title={listError}>加载失败</span>}
          <button
            type="button"
            onClick={() => void reload()}
            className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer"
          >
            <i className="ri-refresh-line mr-1"></i>刷新
          </button>
          {unreadCount > 0 && (
            <span className="flex items-center gap-1.5 bg-red-50 text-red-600 text-xs px-3 py-1.5 rounded-xl">
              <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
              {unreadCount} 条待处理
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-5">
        {[
          { label: "总反馈数", value: stats.total, icon: "ri-chat-3-line", color: "text-gray-600 bg-gray-50" },
          { label: "未读", value: stats.unread, icon: "ri-mail-unread-line", color: "text-red-600 bg-red-50" },
          { label: "处理中", value: stats.processing, icon: "ri-time-line", color: "text-amber-600 bg-amber-50" },
          { label: "已处理", value: stats.resolved, icon: "ri-check-double-line", color: "text-green-600 bg-green-50" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-2 ${s.color}`}>
              <i className={`${s.icon} text-lg`}></i>
            </div>
            <p className="text-2xl font-bold text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
          {(["all", "unread", "processing", "resolved"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-800 font-medium" : "text-gray-500 hover:text-gray-700"}`}
            >
              {s === "all" ? "全部" : STATUS_CONFIG[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-5">
        <div className="flex-1 min-w-0 space-y-2">
          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-chat-3-line text-4xl text-gray-200 block mb-3"></i>
              <p className="text-sm">{listLoading ? "加载中…" : "暂无反馈记录"}</p>
            </div>
          )}
          {filtered.map((item) => {
            const typeCfg = TYPE_CONFIG[item.type];
            const statusCfg = STATUS_CONFIG[item.status];
            return (
              <div
                key={`${item.tenantId ?? "platform"}-${item.id}`}
                onClick={() => void handleSelect(item)}
                className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 ${
                  selectedItem?.id === item.id ? "border-amber-400" : "border-transparent hover:border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${typeCfg.color}`}>
                    <i className={`${typeCfg.icon} text-base`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-semibold text-gray-500">{tenantLabel(item)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${typeCfg.color}`}>{typeCfg.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 flex-shrink-0 ${statusCfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`}></span>
                        {statusCfg.label}
                      </span>
                      <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{item.submittedAt}</span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{item.content}</p>
                    {item.email && (
                      <p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
                        <i className="ri-mail-line"></i> {item.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {selectedItem && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-2xl p-5 sticky top-0 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-800 text-sm">反馈详情</h3>
                <button onClick={() => setSelectedItem(null)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100">
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-xl font-medium flex items-center gap-1 ${TYPE_CONFIG[selectedItem.type].color}`}>
                  <i className={`${TYPE_CONFIG[selectedItem.type].icon} text-sm`}></i>
                  {selectedItem.type}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-xl font-medium flex items-center gap-1 ${STATUS_CONFIG[selectedItem.status].color}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[selectedItem.status].dot}`}></span>
                  {STATUS_CONFIG[selectedItem.status].label}
                </span>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">来源租户</span>
                  <span className="text-gray-700 font-medium truncate max-w-[160px]">{tenantLabel(selectedItem)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">提交时间</span>
                  <span className="text-gray-700 font-medium">{selectedItem.submittedAt}</span>
                </div>
                {selectedItem.email && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">联系邮箱</span>
                    <span className="text-gray-700 font-medium truncate max-w-[150px]">{selectedItem.email}</span>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">反馈内容</p>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-3">{selectedItem.content}</p>
              </div>
              {selectedItem.reply && (
                <div className="bg-green-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-green-700 mb-1.5 flex items-center gap-1">
                    <i className="ri-reply-line"></i> 已回复
                    {selectedItem.handledByLabel ? ` · ${selectedItem.handledByLabel}` : ""} · {selectedItem.repliedAt}
                  </p>
                  <p className="text-xs text-green-700 leading-relaxed">{selectedItem.reply}</p>
                </div>
              )}
              {selectedItem.status !== "resolved" && (
                <button
                  type="button"
                  onClick={() => void handleMarkResolved(selectedItem)}
                  className="w-full py-2.5 bg-green-600 text-white text-sm rounded-xl cursor-pointer hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-check-double-line mr-1.5"></i>标记为已处理
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
